import React, { useState, useEffect, useContext } from "react";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Spinner,
  Alert,
  Badge,
  Card,
  Table,
  ButtonGroup,
} from "react-bootstrap";
import {
  IconShield,
  IconCheck,
  IconX,
  IconRefresh,
  IconUser,
  IconEye,
  IconEdit,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import Swal from "sweetalert2";
import {
  getUser,
  updateUserPermission,
  resetUserPermissions,
} from "../services/UserServicesV2";
import NotificationsContext from "../../../contexts/NotificationsContext";
import { SYSTEM_MODULES, PERMISSION_ACTIONS, SUPERUSER_LEVELS } from "../utils";

const UserPermissionsModal = ({ show, onHide, user, onSave }) => {
  const { setNotify } = useContext(NotificationsContext);
  const [loading, setLoading] = useState(false);
  const [userDetails, setUserDetails] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [availableModules, setAvailableModules] = useState([]);
  const [updatingPermission, setUpdatingPermission] = useState(null);

  useEffect(() => {
    if (show && user) {
      fetchUserDetails();
    }
  }, [show, user]);

  const fetchUserDetails = async () => {
    setLoading(true);
    try {
      const response = await getUser(user.id);
      const userData = response.data;

      setUserDetails(userData);
      setPermissions(userData.permissions || {});
      setAvailableModules(userData.available_modules || []);
    } catch (error) {
      console.error("Erro ao buscar detalhes do usuário:", error);
      setNotify({
        title: "Erro!",
        message: "Erro ao carregar permissões do usuário",
        type: "error",
        visible: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePermissionChange = async (moduleName, action, value) => {
    const permissionKey = `${moduleName}-${action}`;
    setUpdatingPermission(permissionKey);

    try {
      await updateUserPermission(user.id, {
        module_name: moduleName,
        action: action,
        granted: value,
      });

      // Atualiza o estado local
      setPermissions((prev) => ({
        ...prev,
        [moduleName]: {
          ...prev[moduleName],
          [action]: value,
        },
      }));

      setNotify({
        title: "Sucesso!",
        message: `Permissão ${value ? "concedida" : "removida"} com sucesso!`,
        type: "success",
        visible: true,
      });

      // Recarrega dados para garantir sincronização
      setTimeout(() => {
        fetchUserDetails();
      }, 1000);
    } catch (error) {
      console.error("Erro ao atualizar permissão:", error);
      setNotify({
        title: "Erro!",
        message: "Erro ao atualizar permissão",
        type: "error",
        visible: true,
      });
    } finally {
      setUpdatingPermission(null);
    }
  };

  const handleResetPermissions = () => {
    Swal.fire({
      title: "Resetar Permissões?",
      text: "Isso irá remover todas as permissões customizadas e restaurar as permissões padrão do nível do usuário.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Sim, resetar!",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setLoading(true);
        try {
          await resetUserPermissions(user.id);

          setNotify({
            title: "Sucesso!",
            message: "Permissões resetadas para o padrão do nível!",
            type: "success",
            visible: true,
          });

          fetchUserDetails();
          onSave();
        } catch (error) {
          setNotify({
            title: "Erro!",
            message: "Erro ao resetar permissões",
            type: "error",
            visible: true,
          });
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const getPermissionIcon = (action) => {
    const icons = {
      create: IconPlus,
      read: IconEye,
      update: IconEdit,
      delete: IconTrash,
    };
    return icons[action] || IconCheck;
  };

  const getPermissionColor = (action) => {
    const colors = {
      create: "success",
      read: "info",
      update: "warning",
      delete: "danger",
    };
    return colors[action] || "secondary";
  };

  const renderUserInfo = () => {
    if (!userDetails) return null;

    return (
      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex align-items-center">
            {userDetails.photo && (
              <img
                src={userDetails.photo}
                alt={userDetails.name}
                className="rounded-circle me-3"
                width="48"
                height="48"
                style={{ objectFit: "cover" }}
              />
            )}
            <div className="flex-grow-1">
              <h5 className="mb-1">{userDetails.name}</h5>
              <div className="d-flex gap-2 align-items-center">
                <Badge bg={userDetails.is_active ? "success" : "secondary"}>
                  {userDetails.is_active ? "Ativo" : "Inativo"}
                </Badge>
                <Badge bg="primary">
                  {SUPERUSER_LEVELS[userDetails.superuser_level] || "Usuário"}
                </Badge>
              </div>
            </div>
          </div>
        </Card.Body>
      </Card>
    );
  };

  const renderPermissionsTable = () => {
    if (loading) {
      return (
        <div className="text-center p-4">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Carregando permissões...</p>
        </div>
      );
    }

    return (
      <Table responsive striped bordered hover>
        <thead className="table-dark">
          <tr>
            <th style={{ width: "200px" }}>Módulo</th>
            {PERMISSION_ACTIONS.map((action) => (
              <th
                key={action.value}
                className="text-center"
                style={{ width: "120px" }}
              >
                {React.createElement(getPermissionIcon(action.value), {
                  size: 16,
                  className: "me-1",
                })}
                {action.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SYSTEM_MODULES.map((moduleName) => {
            const modulePermissions = permissions[moduleName] || {};

            return (
              <tr key={moduleName}>
                <td className="fw-bold">{moduleName}</td>
                {PERMISSION_ACTIONS.map((action) => {
                  const hasPermission =
                    modulePermissions[action.value] || false;
                  const permissionKey = `${moduleName}-${action.value}`;
                  const isUpdating = updatingPermission === permissionKey;

                  return (
                    <td key={action.value} className="text-center">
                      <Form.Check
                        type="switch"
                        id={permissionKey}
                        checked={hasPermission}
                        disabled={isUpdating}
                        onChange={(e) =>
                          handlePermissionChange(
                            moduleName,
                            action.value,
                            e.target.checked
                          )
                        }
                        className="d-inline-flex justify-content-center"
                      />
                      {isUpdating && (
                        <Spinner
                          size="sm"
                          animation="border"
                          className="ms-2"
                        />
                      )}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center">
          <IconShield className="me-2" />
          Gerenciar Permissões
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {renderUserInfo()}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="mb-0">Permissões por Módulo</h6>
          <Button
            variant="outline-warning"
            size="sm"
            onClick={handleResetPermissions}
            disabled={loading}
            className="d-flex align-items-center"
          >
            <IconRefresh size={16} className="me-1" />
            Resetar para Padrão
          </Button>
        </div>

        <Alert variant="info">
          <strong>Como funciona:</strong>
          <ul className="mb-0 mt-2">
            <li>
              <strong>Visualizar:</strong> Permite ver o módulo e seus dados
            </li>
            <li>
              <strong>Criar:</strong> Permite criar novos registros
            </li>
            <li>
              <strong>Editar:</strong> Permite modificar registros existentes
            </li>
            <li>
              <strong>Excluir:</strong> Permite remover registros
            </li>
          </ul>
        </Alert>

        {renderPermissionsTable()}

        {userDetails && (
          <Alert variant="light" className="mt-3">
            <small className="text-muted">
              <strong>Nível Base:</strong>{" "}
              {SUPERUSER_LEVELS[userDetails.superuser_level]} •
              <strong> Módulos Disponíveis:</strong> {availableModules.length}{" "}
              de {SYSTEM_MODULES.length}
            </small>
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={loading}>
          Fechar
        </Button>
        <Button
          variant="primary"
          onClick={() => {
            onSave();
            onHide();
          }}
          disabled={loading}
          className="d-flex align-items-center"
        >
          <IconCheck size={18} className="me-1" />
          Concluir
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UserPermissionsModal;
